import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import classNames from 'classnames';
import { get, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { createNicePhone, clearPhone } from 'site-modules/shared/utils/phone-format';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { DealerDetailsLogo } from 'site-modules/dealerships/components/dealer-details-logo/dealer-details-logo';
import { DealerFiveStarAwards } from 'site-modules/dealerships/components/dealer-five-star-awards/dealer-five-star-awards';
import { DealerDetailsWorkHours } from 'site-modules/dealerships/components/dealer-details-work-hours/dealer-details-work-hours';
import { MobileLinks } from 'site-modules/shared/components/mobile-links/mobile-links';
import { DealerInfoModel } from 'client/data/models/dealer-info';
import { weekdays } from 'client/utils/weekdays';
import { ActivEngageChatBtn } from 'client/site-modules/dealerships/components/activ-engage-chat-btn/activ-engage-chat-btn';
import { getDealerWebsiteExternalLink } from 'site-modules/shared/utils/dealership-link-constructor';
import { getGoogleMapsUrl } from 'site-modules/shared/utils/dealer-details-utils';
import { showChatButton } from 'site-modules/dealerships/utils/show-chat-button';

import './dealer-details.scss';

const PHONE_PLACEHOLDER = /1{10}/;
const PHONE_COPY = 'Call Dealer';

export class DealerDetailsUI extends Component {
  state = {
    isCTAClicked: false,
  };

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.props.dealerInfo !== nextProps.dealerInfo ||
      get(this.props.dealerChatInfo, 'dealerSmsNumber') !== get(nextProps.dealerChatInfo, 'dealerSmsNumber') ||
      this.state.isCTAClicked !== nextState.isCTAClicked
    );
  }

  /**
   * Returns dealer website link.
   * @returns {String}
   */
  getDealerWebSite = () => {
    const { dealerInfo } = this.props;
    const dealerWebSites = get(dealerInfo, 'dealerWebSites', []);
    const dealerName = get(dealerInfo, 'name', '');
    const city = get(dealerInfo, 'address.city', '');
    const stateCode = get(dealerInfo, 'address.stateCode', '');
    return getDealerWebsiteExternalLink({ dealerWebSites, dealerName, city, stateCode });
  };

  getCurrentDay = () => {
    const date = new Date();
    return weekdays[date.getDay()];
  };

  getDealerPhone = () => {
    const { phones } = this.props.dealerInfo;
    const dealerPhone = phones && phones.length && phones.find(phone => !PHONE_PLACEHOLDER.test(phone));
    return (dealerPhone && dealerPhone.replace(' ', '')) || null;
  };

  /**
   * Change CTA text when it's clicked
   */
  handleClickCTATextChange = e => {
    e.preventDefault();
    this.setState({ isCTAClicked: true });
  };

  createMobileLink = (name, href, tracking = null, target = '_self', rel = null) => ({
    name,
    href,
    target,
    tracking,
    rel,
  });

  createMobileTelLink = ({ name, href, tracking = null, trackingValue = null, onClick }) => ({
    name,
    href,
    tracking,
    trackingValue,
    onClick,
  });

  createMobileLinks = (dealerSmsNumber = '', dealerPhone = '', dealerWebSite = '') => {
    const mobileLinks = [];
    if (dealerSmsNumber) mobileLinks.push(this.createMobileLink('Text Us', `sms:${dealerSmsNumber}`, 'ddp_text_us'));
    if (dealerPhone) {
      const { isCTAClicked } = this.state;
      const nicePhone = createNicePhone(dealerPhone);

      mobileLinks.push(
        this.createMobileTelLink({
          name: isCTAClicked ? nicePhone : PHONE_COPY,
          href: `tel:${clearPhone(dealerPhone)}`,
          tracking: 'ddp_call_dealer',
          trackingValue: nicePhone,
          onClick: isCTAClicked ? null : this.handleClickCTATextChange,
        })
      );
    }
    if (dealerWebSite)
      mobileLinks.push(
        this.createMobileLink(
          'View Dealer Website',
          dealerWebSite,
          'ddp_dealer_site_clickout',
          '_blank',
          'noopener noreferrer nofollow'
        )
      );
    return mobileLinks;
  };

  render() {
    const { logoUrl, fiveStarDealerAward, workHours, address, parentDealershipName } = this.props.dealerInfo;
    const { dealerSmsNumber, isEdmundsChatActive } = this.props.dealerChatInfo;
    const { isCTAClicked } = this.state;
    const { isMobile, hasShowroomPhotos, isSuppressedMobileLinks } = this.props;

    const dealerWebSite = this.getDealerWebSite();
    const currentDay = this.getCurrentDay();
    const dealerPhone = this.getDealerPhone();
    const hasChatButton = showChatButton({
      isMobile,
      parentDealershipName,
      dealerSmsNumber,
      isEdmundsChatActive,
    });
    const hasFiveStarDealerAward = !!(fiveStarDealerAward && fiveStarDealerAward.length);

    // for mobile with dealer photos photos we show the address in the carousel (dealer-carousel-slide.jsx)
    const showAddressInCarousel = hasShowroomPhotos && isMobile;

    // show the hours next to the five-star-dealer badge or logo for mobile when the address is in the carousel
    // and when there is only either the five-star-dealer or the logo.  If there is both the five-star dealer or
    // the logo, then the dealer hours should take up a full line.
    const showHoursInHalfColumn = showAddressInCarousel && !logoUrl !== !hasFiveStarDealerAward;
    let nicePhone;
    let phoneClear;

    if (dealerPhone) {
      nicePhone = createNicePhone(dealerPhone);
      phoneClear = clearPhone(dealerPhone);
    }

    return (
      <Container className="dealer-details">
        <Row className="justify-content-around dealer-details-row medium">
          {(logoUrl || dealerWebSite) && (
            <Col
              md={3}
              xs={hasFiveStarDealerAward || showAddressInCarousel ? 6 : 12}
              className={classNames('text-center dealer-details-column', { 'hidden-md-down': !logoUrl })}
            >
              <DealerDetailsLogo dealerWebSite={dealerWebSite} logoUrl={logoUrl} isMobile={isMobile} />
            </Col>
          )}
          {hasFiveStarDealerAward && (
            <Col xs={logoUrl || showAddressInCarousel ? 6 : 12} className="hidden-md-up text-center">
              <DealerFiveStarAwards className="d-inline-block" years={fiveStarDealerAward} />
            </Col>
          )}
          {address && !showAddressInCarousel && (
            <Col
              md={dealerPhone || hasChatButton ? 3 : 4}
              xs={12}
              className="dealer-details-address pt-1 dealer-details-column"
            >
              <a
                className="d-inline-block"
                href={getGoogleMapsUrl(address)}
                rel="noopener noreferrer nofollow"
                data-tracking-id="ddp_view_dealer_hours_and_directions"
                target="_blank"
                data-testid="dealer-address"
              >
                <span className="icon-location5 float-start mt-0_25" aria-hidden />
                <div className="text-start ms-1_5">
                  {address.street}, {address.city}, {address.stateCode}&nbsp;{address.zip}
                </div>
              </a>
            </Col>
          )}
          {(dealerPhone || hasChatButton) && (
            <Col
              lg={hasChatButton ? 3 : 2}
              md={3}
              xs={12}
              className={classNames('pt-1 dealer-details-column text-center', { 'hidden-md-down': !hasChatButton })}
            >
              {dealerPhone && (
                <a
                  href={`tel:${phoneClear}`}
                  data-tracking-id="ddp_call_dealer"
                  className="hidden-md-down d-inline-block mb-1"
                  onClick={isCTAClicked ? null : this.handleClickCTATextChange}
                  data-tracking-value={nicePhone}
                  data-testid="dealer-phone"
                >
                  <span className="icon-phone2" aria-hidden />
                  <span className="ps-0_5" style={{ paddingLeft: '8px!important' }}>
                    {isCTAClicked ? nicePhone : PHONE_COPY}
                  </span>
                </a>
              )}
              {hasChatButton && (
                <div>
                  <ActivEngageChatBtn launchSource="edmunds-ddp" dealerWebSite={dealerWebSite} />
                </div>
              )}
            </Col>
          )}
          {!isEmpty(workHours) && (
            <Col
              lg={hasChatButton ? 3 : 4}
              md={dealerPhone || hasChatButton ? 3 : 4}
              xs={showHoursInHalfColumn ? 6 : 12}
              className="dealer-details-column pt-1"
            >
              <span className="icon-clock float-start mb-1 mt-0_25" aria-hidden />
              <div className="text-start ms-1_5">
                <DealerDetailsWorkHours
                  currentDay={currentDay}
                  workHours={workHours}
                  showHoursInHalfColumn={showHoursInHalfColumn}
                  dealerZip={!!address && address.zip}
                />
              </div>
            </Col>
          )}
        </Row>
        {!isSuppressedMobileLinks && (
          <MobileLinks
            linksList={this.createMobileLinks(dealerSmsNumber, dealerPhone, dealerWebSite)}
            className="hidden-md-up mt-1_5"
          />
        )}
      </Container>
    );
  }
}

DealerDetailsUI.propTypes = {
  dealerInfo: PropTypes.shape({
    logoUrl: PropTypes.string,
    currentDay: PropTypes.string,
    fiveStarDealerAward: PropTypes.arrayOf(PropTypes.number),
    dealerWebSites: PropTypes.arrayOf(PropTypes.string),
    workHours: PropTypes.shape({
      monday: PropTypes.shape({
        open: PropTypes.string,
        close: PropTypes.string,
      }),
      tuesday: PropTypes.shape({
        open: PropTypes.string,
        close: PropTypes.string,
      }),
      wednesday: PropTypes.shape({
        open: PropTypes.string,
        close: PropTypes.string,
      }),
      thursday: PropTypes.shape({
        open: PropTypes.string,
        close: PropTypes.string,
      }),
      friday: PropTypes.shape({
        open: PropTypes.string,
        close: PropTypes.string,
      }),
      saturday: PropTypes.shape({
        open: PropTypes.string,
        close: PropTypes.string,
      }),
      sunday: PropTypes.shape({
        open: PropTypes.string,
        close: PropTypes.string,
      }),
    }),
    phones: PropTypes.arrayOf(PropTypes.string),
    address: PropTypes.shape({
      city: PropTypes.string,
      stateCode: PropTypes.string,
      stateName: PropTypes.string,
      street: PropTypes.string,
      zip: PropTypes.string,
    }),
    parentDealershipName: PropTypes.string,
    id: PropTypes.number,
  }),
  dealerChatInfo: PropTypes.shape({
    dealerSmsNumber: PropTypes.string,
    isEdmundsChatActive: PropTypes.bool,
  }),
  hasShowroomPhotos: PropTypes.bool,
  isMobile: PropTypes.bool,
  isSuppressedMobileLinks: PropTypes.bool,
};
DealerDetailsUI.defaultProps = {
  dealerInfo: {
    id: null,
    logoUrl: '',
    currentDay: '',
    fiveStarDealerAward: [],
    dealerWebSites: [],
    workHours: null,
    phones: [],
    address: null,
    parentDealershipName: '',
  },
  dealerChatInfo: { dealerSmsNumber: '', isEdmundsChatActive: false },
  hasShowroomPhotos: false,
  isMobile: false,
  isSuppressedMobileLinks: false,
};

export const stateToPropsConfig = {
  dealerChatInfo: {
    path: props => {
      const { dealerInfo } = props;
      const rooftopId = dealerInfo && dealerInfo.rooftopId;
      return rooftopId ? `carcodeConfig.${rooftopId}` : '';
    },
    segment: DealerInfoModel,
    transform: ({ smsNumber, isEdmundsChatActive }) => ({ dealerSmsNumber: smsNumber, isEdmundsChatActive }),
  },
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const DealerDetails = connect(mapStateToProps)(connectToModel(DealerDetailsUI, stateToPropsConfig));

DealerDetails.propTypes = {
  ...DealerDetailsUI.propTypes,
};
